import {
  createContext,
  createRef,
  Dispatch,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  PropsWithoutRef,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Button from "@/components/Button";
// oh no a circular dependency
import { WORKBENCH_INTERACTION_SEGMENT_MESSAGE } from "@/components/rules-editor/Workbench";
import Coachmark from "@/components/Coachmark";
import InlineCode from "@/components/InlineCode";
import { useSegmentCallbackV2 } from "@/src/segment";
import {
  checkIfValidResource,
  decapitalize,
  EXAMPLE_USER_NAMES,
  usePersistedExtraWorkbenchState,
  WorkbenchPolicy,
  WorkbenchPolicyAction,
} from "@/src/workbenchLogic";
import Modal from "@/components/Modal";
import { EnvData, Option } from "@/src/types";
import { Combobox } from "@headlessui/react";
import { roleGrantsPermission } from "@/src/workbenchLogic";

// TODO: flows must be able to select tabs in editor panels

export type WizardTarget = string;

// Note: keys in JavaScript Maps get automatically converted to strings,
// so if we want to use a richer key type, we should do the stringification
// ourselves rather than changing this key type.
const refStore: Map<string, RefObject<any>> = new Map();

/** pure function */
export function wizardRef<T extends Element>(
  target: WizardTarget
): RefObject<T> {
  const storedRef = refStore.get(target);
  if (storedRef !== undefined) return storedRef;
  const newRef = createRef<T>();
  refStore.set(target, newRef);
  // Cast to RefObject since these are technically `null` during the first
  // render, but are updated during the commit phase.
  return newRef as RefObject<T>;
}

export const WizardTarget: FC<
  { target: WizardTarget } & PropsWithoutRef<HTMLAttributes<HTMLDivElement>>
> = ({ target, ...props }) => {
  return <div ref={wizardRef<HTMLDivElement>(target)} {...props}></div>;
};

/** scrolls the given target into view and highlights it momentarily */
export function showNewTarget(
  target: WizardTarget,
  retryDelay = 10,
  retries = 3
) {
  const ref = wizardRef(target);
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth" });
    // NOTE: this is a bit hacky, as it adds/removes classes in a way that circumvents React.
    // If the element is rerendered after we add the shadow, the shadow will disappear early.
    ref.current.classList.add("shadow-2xl", "shadow-oso-light-blue");
    setTimeout(() => {
      ref.current?.classList.remove("shadow-2xl", "shadow-oso-light-blue");
    }, 3000);
  } else if (retries > 0) {
    setTimeout(
      () => showNewTarget(target, retryDelay, retries - 1),
      retryDelay
    );
  }
}

export const WizardFlowContext = createContext<{
  flow: Flow | null;
  target: RefObject<Element> | null;
  /** sets the flow and clears the target */
  pushFlow(flow: Flow | null): void;
  setTarget<T extends Element>(
    target: SetStateAction<RefObject<T> | null>
  ): void;
}>({
  flow: null,
  target: null,
  pushFlow(flow) {
    console.error("WizardStateContext::pushFlow used without a provider", flow);
  },
  setTarget(target) {
    console.error(
      "WizardStateContext::setTarget used without a provider",
      target
    );
  },
});

/** provides child elements with a setter for the wizard state */
export const WizardFlowProvider: FC<PropsWithChildren> = ({ children }) => {
  const [flow, setFlow] = useState<Flow | null>(null);
  const [target, setTarget] = useState<RefObject<any> | null>(null);
  const segmentCallback = useSegmentCallbackV2();
  return (
    <WizardFlowContext.Provider
      value={{
        flow: flow,
        target,
        pushFlow(flow: Flow | null) {
          segmentCallback(WORKBENCH_INTERACTION_SEGMENT_MESSAGE, {
            interaction: "Workbench Wizard Flow Update",
            flow: flow?.name,
          });
          // wrap in `() =>` because since it's a react component, it's a function,
          // so the setter interprets it as a state updater function
          setFlow(() => flow);
          setTarget(null);
        },
        setTarget,
      }}
    >
      {children}
    </WizardFlowContext.Provider>
  );
};

/** supplied automatically by the wizard framework */
type FlowProps = {
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
  setTarget(target: RefObject<Element>): void;
  pushFlow(flow: Flow | null): void;
};

type Flow = FC<FlowProps>;

const GLOBAL_ROLES_TAB_INDEX = 2;
const PERMISSIONS_AND_ROLES_TAB_INDEX = 0;

function groupsRule(groupActor: string): string {
  const groupVariable = decapitalize(groupActor);
  return `has_role(actor: Actor, role: String, resource: Resource) if
  ${groupVariable} matches ${groupActor} and
  has_group(actor, ${groupVariable}) and
  has_role(${groupVariable}, role, resource);`;
}

/** Provides a ref such that whatever DOM node you place inside the ref,
 *  that node's `focus` method will be called when the component is rendered.
 *  Useful to put on buttons so that `Enter` selects the button */
function useFocusOnRenderRef<T extends HTMLElement>(): RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  // Cast to RefObject since these are technically `null` during the first
  // render, but are updated during the commit phase.
  return ref as RefObject<T>;
}

/** FLOWS **/

export function confirmClobberFlow(
  alreadyExists: string,
  clobber: () => void
): Flow {
  return function ConfirmClobberFlow({ pushFlow }) {
    const ref = useFocusOnRenderRef<HTMLButtonElement>();
    return (
      <div>
        <InlineCode>{alreadyExists}</InlineCode> already exists. Would you like
        to replace it?
        <div className="flex justify-around">
          <Button
            medium
            onClick={() => {
              pushFlow(null);
            }}
          >
            Cancel
          </Button>
          <Button
            medium
            theme="primary"
            onClick={() => {
              pushFlow(null);
              clobber();
            }}
            ref={ref}
          >
            Replace
          </Button>
        </div>
      </div>
    );
  };
}

function addGlobalRolesFlow(resource: number): Flow {
  return function AddGlobalRolesFlow({
    workbenchPolicy,
    setTarget,
    pushFlow,
    selectedEnv,
  }) {
    const [{ [workbenchPolicy.resources[resource].name]: selectedTab }] =
      usePersistedExtraWorkbenchState(selectedEnv, "selectedTabs", {});
    if (workbenchPolicy.global) {
      pushFlow(addGlobalRolesFlowPartTwo(resource));
    } else if (selectedTab !== GLOBAL_ROLES_TAB_INDEX) {
      setTarget(
        wizardRef(`resources.${resource}.tabs.${GLOBAL_ROLES_TAB_INDEX}`)
      );
    } else {
      // selectedTab === GLOBAL_ROLES_TAB_INDEX
      setTarget(
        wizardRef(
          `resources.${resource}.tabs.${GLOBAL_ROLES_TAB_INDEX}.buttons.0`
        )
      );
    }
    return (
      <div>
        Add <span className="font-space">Global Roles</span>.
      </div>
    );
  };
}

/** precondition: there is a global block */
function addGlobalRolesFlowPartTwo(resource: number): Flow {
  return function AddGlobalRolesFlowPartTwo({
    setTarget,
    pushFlow,
    workbenchPolicy,
  }) {
    const buttonRef = useFocusOnRenderRef<HTMLButtonElement>();
    setTarget(wizardRef("global"));
    const exampleRole = workbenchPolicy.global?.roles[0] ?? "admin";
    return (
      <div>
        The columns in this table configure{" "}
        <span className="font-space">global roles</span>. Global roles grant
        users system-wide permissions. For example, to grant a user the{" "}
        <InlineCode>{`"${exampleRole}"`}</InlineCode> role, you would add the
        fact{" "}
        <InlineCode>{`has_role(User{"bob"}, "${exampleRole}")`}</InlineCode>.
        <div className="flex justify-end items-center">
          <Button
            theme="primary"
            medium
            onClick={() => pushFlow(addGlobalRolesFlowPartThree(resource))}
            ref={buttonRef}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  };
}

function addGlobalRolesFlowPartThree(resource: number): Flow {
  return function AddGlobalRolesFlowPartThree({
    setTarget,
    pushFlow,
    selectedEnv,
    workbenchPolicy,
  }) {
    const [{ [workbenchPolicy.resources[resource].name]: selectedTab }] =
      usePersistedExtraWorkbenchState(selectedEnv, "selectedTabs", {});
    if (selectedTab === GLOBAL_ROLES_TAB_INDEX) {
      pushFlow(addGlobalRolesFlowPartFour(resource));
    }
    setTarget(
      wizardRef(`resources.${resource}.tabs.${GLOBAL_ROLES_TAB_INDEX}`)
    );
    return (
      <div>
        Go to the <span className="font-space">Global Roles</span> tab.
      </div>
    );
  };
}

/** precondition: the resource's global roles tab is open */
function addGlobalRolesFlowPartFour(resource: number): Flow {
  return function AddGlobalRolesFlowPartFour({
    setTarget,
    pushFlow,
    workbenchPolicy,
  }) {
    setTarget(wizardRef(`resources.${resource}`));
    const buttonRef = useFocusOnRenderRef<HTMLButtonElement>();
    return (
      <div>
        Use this table to configure what a user{"'"}s global role grants them on{" "}
        <InlineCode>{workbenchPolicy.resources[resource].name}</InlineCode>s.
        <div className="flex justify-end items-center">
          <Button
            theme="primary"
            medium
            onClick={() => pushFlow(null)}
            ref={buttonRef}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };
}

function addSharingFlow(resource: number): Flow {
  return function AddSharingFlow({
    workbenchPolicy,
    setTarget,
    pushFlow,
    selectedEnv,
  }) {
    // 1. prompt the user to navigate to the "permissions and roles" tab if it's not already open
    // 2. prompt the user to create "invite" permission if it does not exist
    // 3. prompt the user to create the "owner" role if it does not exist
    // 4. prompt the user to give the "owner" role, the "invite" permission (if "owner" exists)
    // 5. explain that the user needs to grant permission when an "invite" action occurs

    // get tab for selected resource
    const [{ [workbenchPolicy.resources[resource].name]: selectedTab }] =
      usePersistedExtraWorkbenchState(selectedEnv, "selectedTabs", {});

    if (selectedTab === PERMISSIONS_AND_ROLES_TAB_INDEX) {
      pushFlow(addSharingFlowPartTwo(resource));
    } else {
      setTarget(
        wizardRef(
          `resources.${resource}.tabs.${PERMISSIONS_AND_ROLES_TAB_INDEX}`
        )
      );
    }

    return (
      <div>
        Navigate to the{" "}
        <span className="font-space">Permissions and Roles</span> tab.
      </div>
    );
  };
}

// NOTE: since this isn't related to a resource,
// we should explore if we can expose this flow in a different way
// than from a resource block panel
function addGroupsFlow(_resource: number): Flow {
  return function AddGroupsFlow({ workbenchPolicy, dispatch, pushFlow }) {
    const [selectedGroupActor, setSelectedGroupActor] = useState("Group");
    const existingActors = workbenchPolicy.actors.map((actor) => actor.name);
    const options = [...existingActors];
    if (!existingActors.includes(selectedGroupActor)) {
      options.push(selectedGroupActor);
    }
    const selectedGroupActorNameIsInvalid: Option<string> =
      checkIfValidResource(workbenchPolicy.resources, selectedGroupActor);
    return (
      <div>
        <div className="text-2xl font-bold font-space">User Groups</div>
        Organizing your users into groups allows you to grant a role to many
        users at once. Let{"'"}s start by creating (or selecting) a type to
        represent groups of users.
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            const existingGroupActor =
              existingActors.indexOf(selectedGroupActor);
            if (existingGroupActor === -1) {
              dispatch({ action: "add actor", name: selectedGroupActor });
              pushFlow(addGroupsFlowPartTwo(workbenchPolicy.actors.length));
            } else {
              pushFlow(addGroupsFlowPartTwo(existingGroupActor));
            }
          }}
        >
          <div className="relative mt-4 self-center">
            <Combobox
              value={selectedGroupActor}
              onChange={(value) =>
                // Narrow the value type since we don't allow this Combobox to
                // be cleared
                value !== null && setSelectedGroupActor(value)
              }
            >
              <Combobox.Input
                onChange={(e) => setSelectedGroupActor(e.target.value)}
                className="px-2 py-1 rounded border"
              />
              <Combobox.Button className="absolute inset-0" />
              <Combobox.Options className="absolute top-full inset-x-0 rounded">
                <div className="absolute top-full rounded bg-red-100 font-red-800 shadow-md">
                  {selectedGroupActorNameIsInvalid}
                </div>
                {options.map((option) => (
                  <Combobox.Option value={option} key={option}>
                    {({ active, selected }) => (
                      <div
                        className={`px-2 py-1 flex justify-between items-center ${
                          active ? "bg-gray-100" : "bg-white"
                        } ${selected ? "font-bold" : ""}`}
                      >
                        <div>{option}</div>
                        {existingActors.includes(option) || (
                          <div className="text-xs text-gray-500">(create)</div>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
              {selectedGroupActorNameIsInvalid && (
                <div className="absolute top-full rounded bg-red-100 text-red-800 p-4 shadow">
                  {selectedGroupActorNameIsInvalid}
                </div>
              )}
            </Combobox>
          </div>
          <div className="flex justify-end items-center mt-4">
            <Button
              type="submit"
              theme="primary"
              medium
              disabled={!!selectedGroupActorNameIsInvalid}
            >
              {existingActors.includes(selectedGroupActor)
                ? `Select`
                : `Create`}
            </Button>
          </div>
        </form>
      </div>
    );
  };
}

/** precondition: permissions and roles tab is open */
function addSharingFlowPartTwo(resource: number): Flow {
  return function AddSharingFlowPartTwo({
    workbenchPolicy,
    setTarget,
    pushFlow,
  }) {
    const resourcePerms: string[] = workbenchPolicy.resources[resource].perms;
    if (resourcePerms.includes("invite")) {
      pushFlow(addSharingFlowPartThree(resource));
    } else {
      setTarget(wizardRef(`resources.${resource}.rolePermissionMatrix.addRow`));
    }

    return (
      <div>
        Add the <span className="font-space">{'"invite"'}</span> permission.
      </div>
    );
  };
}

/** precondition: groupActor is declared as an actor */
function addGroupsFlowPartTwo(groupActor: number): Flow {
  return function AddGroupsFlowPartTwo({
    workbenchPolicy,
    dispatch,
    setTarget,
    pushFlow,
  }) {
    setTarget(wizardRef(`actors.${groupActor}`));
    const rule = {
      description: `Actors inherit roles from their ${workbenchPolicy.actors[groupActor].name}`,
      source: groupsRule(workbenchPolicy.actors[groupActor].name),
    };
    const ruleExists = workbenchPolicy.rules.some(
      (existing) => existing.description === rule.description
    );
    return (
      <div>
        Objects of type{" "}
        <InlineCode>{workbenchPolicy.actors[groupActor].name}</InlineCode>{" "}
        represent groups of users.
        {ruleExists ? (
          <div className="mt-4 flex justify-end">
            <Button
              theme="primary"
              medium
              onClick={(e) => {
                e.preventDefault();
                pushFlow(null);
              }}
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex justify-end">
            <Button
              theme="primary"
              medium
              onClick={(e) => {
                e.preventDefault();
                dispatch({
                  action: "add rule",
                  ...rule,
                });
                pushFlow(
                  addGroupsFlowPartThree(
                    groupActor,
                    workbenchPolicy.rules.length
                  )
                );
              }}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    );
  };
}

/** precondition: "invite" permission exists */
function addSharingFlowPartThree(resource: number): Flow {
  return function AddSharingFlowPartThree({
    workbenchPolicy,
    setTarget,
    pushFlow,
  }) {
    const resourceRoles: string[] = workbenchPolicy.resources[resource].roles;
    if (resourceRoles.includes("owner")) {
      pushFlow(addSharingFlowPartFour(resource));
    } else {
      setTarget(wizardRef(`resources.${resource}.rolePermissionMatrix.addCol`));
    }

    return (
      <div>
        Add an <span className="font-space">owner</span> role.
      </div>
    );
  };
}

/** precondition: rule is declared as a rule */
function addGroupsFlowPartThree(groupActor: number, rule: number): Flow {
  return function AddGroupsFlowPartThree({
    workbenchPolicy,
    dispatch,
    setTarget,
    pushFlow,
  }) {
    setTarget(wizardRef(`rules.${rule}`));
    const testName = `${workbenchPolicy.actors[groupActor].name} user groups`;
    // need to check if the test exists, because duplicate test names are not allowed
    const testExists = workbenchPolicy.tests.some(
      (test) => test.name === testName
    );
    const [exampleResourceForTest] = workbenchPolicy.resources;

    // find valid example role and permission for generated test
    let exampleRoleForTest: Option<number> = null;
    let examplePermForTest: Option<number> = null;
    for (
      let i = 0;
      i <
      exampleResourceForTest.roles.length * exampleResourceForTest.perms.length;
      i++
    ) {
      const role = i % exampleResourceForTest.roles.length;
      const perm = Math.floor(i / exampleResourceForTest.roles.length);
      if (roleGrantsPermission(exampleResourceForTest, role, perm)) {
        exampleRoleForTest = role;
        examplePermForTest = perm;
        break;
      }
    }

    return (
      <div>
        This rule lets you grant all members of a group access to something at
        once by simply granting their group the access.
        {testExists ||
        exampleRoleForTest === null ||
        examplePermForTest === null ? (
          <div className="mt-4 flex justify-end">
            <Button
              theme="primary"
              medium
              onClick={(e) => {
                e.preventDefault();
                pushFlow(null);
              }}
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex justify-end">
            <Button
              theme="primary"
              medium
              onClick={(e) => {
                e.preventDefault();
                const block = workbenchPolicy.tests.length;
                const actorId =
                  EXAMPLE_USER_NAMES[
                    Math.floor(Math.random() % EXAMPLE_USER_NAMES.length)
                  ];
                if (
                  exampleRoleForTest === null ||
                  examplePermForTest === null
                ) {
                  console.error("unexpected null role or perm");
                  pushFlow(null);
                  return;
                }
                dispatch({
                  action: "batch",
                  actions: [
                    {
                      action: "add test block",
                      name: testName,
                      replace: false,
                    },
                    {
                      action: "add test setup fact",
                      block,
                      fact: {
                        predicate: "has_group",
                        args: [
                          {
                            type: workbenchPolicy.actors[0].name,
                            id: actorId,
                          },
                          {
                            type: workbenchPolicy.actors[groupActor].name,
                            id: "group",
                          },
                        ],
                      },
                    },
                    {
                      action: "add test setup fact",
                      block,
                      fact: {
                        predicate: "has_role",
                        args: [
                          {
                            type: workbenchPolicy.actors[groupActor].name,
                            id: "group",
                          },
                          {
                            type: "String",
                            id: exampleResourceForTest.roles[
                              exampleRoleForTest
                            ],
                          },
                          {
                            type: exampleResourceForTest.name,
                            id: "resource",
                          },
                        ],
                      },
                    },
                    {
                      action: "add test assertion",
                      block,
                      assertion: {
                        expected: true,
                        fact: {
                          predicate: "allow",
                          args: [
                            {
                              type: workbenchPolicy.actors[0].name,
                              id: actorId,
                            },
                            {
                              type: "String",
                              id: exampleResourceForTest.perms[
                                examplePermForTest
                              ],
                            },
                            {
                              type: exampleResourceForTest.name,
                              id: "resource",
                            },
                          ],
                        },
                      },
                    },
                  ],
                });
                pushFlow(addGroupsFlowPartFour(block));
              }}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    );
  };
}

/** precondition: "owner" role exists */
function addSharingFlowPartFour(resource: number): Flow {
  return function AddSharingFlowPartFour({
    workbenchPolicy,
    setTarget,
    pushFlow,
  }) {
    const resourcePerms: string[] = workbenchPolicy.resources[resource].perms;
    const resourceRoles: string[] = workbenchPolicy.resources[resource].roles;
    const inviteIdx = resourcePerms.indexOf("invite");
    const ownerIdx = resourceRoles.indexOf("owner");

    const ownerImpliesInvite =
      workbenchPolicy.resources[resource].matrix[inviteIdx][ownerIdx];

    if (ownerImpliesInvite) {
      pushFlow(addSharingFlowPartFive(resource));
    } else {
      setTarget(
        wizardRef(
          `resources.${resource}.rolePermissionMatrix.${inviteIdx}.${ownerIdx}`
        )
      );
    }

    return (
      <div>
        Grant the{" "}
        <span className="font-space">
          {'"'}invite{'"'}
        </span>{" "}
        permission to the{" "}
        <span className="font-space">
          {'"'}owner{'"'}
        </span>{" "}
        role.
      </div>
    );
  };
}

/** precondition: "owner" implies "invite" */
function addSharingFlowPartFive(resource: number): Flow {
  return function AddSharingFlowPartFive({ workbenchPolicy, setTarget }) {
    setTarget(wizardRef(`resources.${resource}`));
    const resourceName = workbenchPolicy.resources[resource].name;

    return (
      <>
        <div>
          Congratulations, you{"'"}ve introduced the ability to share this
          resource!
        </div>
        <div>
          An{" "}
          <span className="font-space">
            {'"'}owner{'"'}
          </span>{" "}
          of this resource now has permission to authorize an{" "}
          <span className="font-space">
            {'"'}invite{'"'}
          </span>
          .
        </div>
        <div>
          Whenever a user successfully authorizes an{" "}
          <span className="font-space">
            {'"'}invite{'"'}
          </span>
          , it{"'"}s up to your application to grant the invited user
          permission. You can accomplish this by adding a fact, such as{" "}
          <InlineCode>
            {`has_role(User{"bob"}, "view", ${resourceName}{"foo"})`}
          </InlineCode>
          .
        </div>
      </>
    );
  };
}

/** precondition: test has been declared as a test */
function addGroupsFlowPartFour(test: number): Flow {
  return function AddGroupsFlowPartFour({ setTarget, pushFlow }) {
    setTarget(wizardRef(`tests.${test}`));
    return (
      <div>
        This test ensures that granting a group access to a resource grants its
        members access to that resource. Feel free to edit it to try out some
        different scenarios.
        <div className="mt-4 flex justify-end">
          <Button
            theme="primary"
            medium
            onClick={(e) => {
              e.preventDefault();
              pushFlow(null);
            }}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };
}

export function addLogicFlow(resource: number): Flow {
  return function AddLogicFlow({ workbenchPolicy, pushFlow }: FlowProps) {
    return (
      <div>
        <div className="text-2xl font-bold font-space w-full">
          Let{"'"}s add logic to{" "}
          <code className="border rounded border-gray-300 bg-gray-100 text-oso-fact-red px-1 py-0.5">
            {workbenchPolicy.resources[resource].name}
          </code>
        </div>
        <div>
          Which of the following best describes what you{"'"}d like to do?
        </div>
        <div className="flex flex-col space-y-2">
          <Button onClick={() => pushFlow(addGlobalRolesFlow(resource))}>
            <div>
              Some users may have system-wide roles that grant access to{" "}
              <InlineCode>
                {workbenchPolicy.resources[resource].name}
              </InlineCode>
              s
            </div>
          </Button>
          <Button onClick={() => pushFlow(addSharingFlow(resource))}>
            <div>
              Users may invite other users to{" "}
              <InlineCode>
                {workbenchPolicy.resources[resource].name}
              </InlineCode>
              s they own
            </div>
          </Button>
          <Button onClick={() => pushFlow(addGroupsFlow(resource))}>
            Access can be granted to groups of users, not just individuals
          </Button>
          <Button
            onClick={() => {
              pushFlow(null);
              // TODO(gj): ripping out Intercom but this seems unrelated to the
              // rest of the Intercom bits so I'm leaving it here for posterity.
              // I don't think this code is actually in-use and should probably
              // be removed, but I'm not going to shave that yak right now.
              //
              // Intercom("trackEvent", "workbench-add-something-else");
              // Intercom(
              //   "showNewMessage",
              //   "I would like help with using Oso to model the following authorization logic: "
              // );
            }}
          >
            Something else...
          </Button>
        </div>
      </div>
    );
  };
}

const Flow: FC<{
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
  Flow: Flow;
  pushFlow: (flow: Flow | null) => void;
  target: RefObject<Element> | null;
  setTarget: (target: RefObject<Element> | null) => void;
  isOpen: boolean;
  listenForScroll: RefObject<Element | null>[];
}> = ({
  Flow,
  target,
  isOpen,
  pushFlow,
  selectedEnv,
  workbenchPolicy,
  dispatch,
  setTarget,
  listenForScroll,
}) => {
  const [isOpenState, setIsOpenState] = useState(false);
  useEffect(() => {
    setIsOpenState(true);
  }, []);
  useEffect(() => {
    target?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [target]);
  return target ? (
    <Coachmark
      isOpen={isOpen && isOpenState}
      target={target}
      dismiss={pushFlow.bind(null, null)}
      listenForScroll={listenForScroll}
    >
      <Flow
        selectedEnv={selectedEnv}
        dispatch={dispatch}
        workbenchPolicy={workbenchPolicy}
        setTarget={setTarget}
        pushFlow={pushFlow}
      />
    </Coachmark>
  ) : (
    <Modal
      isOpen={isOpen && isOpenState}
      onClose={pushFlow.bind(null, null)}
      blue
      showCloseButton
    >
      <Flow
        selectedEnv={selectedEnv}
        dispatch={dispatch}
        workbenchPolicy={workbenchPolicy}
        setTarget={setTarget}
        pushFlow={pushFlow}
      />
    </Modal>
  );
};

export const Wizard: FC<{
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
  listenForScroll: RefObject<Element | null>[];
}> = ({ workbenchPolicy, dispatch, selectedEnv, listenForScroll }) => {
  // we store the state of the wizard in context rather than in component state
  // so that we can hoist the state up to the workbench so that we can trigger
  // the wizard from there
  const {
    flow,
    target,
    pushFlow: pushFlowToContext,
    setTarget,
  } = useContext(WizardFlowContext);
  const [flowKey, setFlowKey] = useState(0);

  const [{ prevFlow, prevTarget }, setPrevFlow] = useState<{
    prevFlow: Option<Flow>;
    prevTarget: Option<RefObject<Element>>;
  }>({
    prevFlow: null,
    prevTarget: null,
  });

  const pushFlow = (newFlow: Flow | null) => {
    setPrevFlow({
      prevFlow: flow,
      prevTarget: target,
    });
    setFlowKey(flowKey + 1);
    pushFlowToContext(newFlow);
  };

  return (
    <>
      {prevFlow && (
        <Flow
          key={flowKey - 1}
          selectedEnv={selectedEnv}
          workbenchPolicy={workbenchPolicy}
          dispatch={dispatch}
          setTarget={setTarget}
          pushFlow={pushFlow}
          target={prevTarget}
          Flow={prevFlow}
          isOpen={false}
          listenForScroll={listenForScroll}
        />
      )}
      {flow && (
        <Flow
          key={flowKey}
          selectedEnv={selectedEnv}
          workbenchPolicy={workbenchPolicy}
          dispatch={dispatch}
          setTarget={setTarget}
          pushFlow={pushFlow}
          target={target}
          Flow={flow}
          isOpen={true}
          listenForScroll={listenForScroll}
        />
      )}
    </>
  );
};
